import React, { useMemo } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";

import { useHotkeyContext } from "./HotkeyContext";
import { type HotkeyDefinition } from "./types";

const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

/** Format a key combo for display (e.g. "mod+b" → "⌘B" on Mac, "Ctrl+B" on Windows) */
const formatKey = (key: string): string => {
  return key
    .split("+")
    .map((part) => {
      const p = part.trim().toLowerCase();
      switch (p) {
        case "mod":
        case "meta":
        case "cmd":
          return isMac ? "⌘" : "Ctrl";
        case "ctrl":
          return isMac ? "⌃" : "Ctrl";
        case "alt":
          return isMac ? "⌥" : "Alt";
        case "shift":
          return isMac ? "⇧" : "Shift";
        case "backspace":
          return "⌫";
        case "delete":
          return "Del";
        case "up":
          return "↑";
        case "down":
          return "↓";
        case "left":
          return "←";
        case "right":
          return "→";
        case "?":
          return "?";
        default:
          return p.toUpperCase();
      }
    })
    .join(isMac ? "" : "+");
};

/** Format keys for display, joining multiple combos with " / " */
const formatKeys = (keys: string | string[]): string => {
  const keyList = Array.isArray(keys) ? keys : [keys];
  // Group arrow keys together
  const arrows = keyList.filter((k) =>
    ["up", "down", "left", "right"].includes(k)
  );
  const others = keyList.filter(
    (k) => !["up", "down", "left", "right"].includes(k)
  );

  const parts: string[] = [];
  if (arrows.length > 0) {
    parts.push(arrows.map(formatKey).join(" "));
  }
  // Group delete/backspace
  const delKeys = others.filter((k) => ["delete", "backspace"].includes(k));
  const rest = others.filter((k) => !["delete", "backspace"].includes(k));
  if (delKeys.length > 0) {
    parts.push(delKeys.map(formatKey).join(" / "));
  }
  for (const k of rest) {
    parts.push(formatKey(k));
  }
  return parts.join(" / ");
};

interface HotkeyHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const groupByCategory = (
  hotkeys: HotkeyDefinition[]
): Record<string, HotkeyDefinition[]> => {
  const grouped: Record<string, HotkeyDefinition[]> = {};
  for (const def of hotkeys) {
    if (!grouped[def.category]) grouped[def.category] = [];
    grouped[def.category].push(def);
  }
  return grouped;
};

const HotkeyHelpModal: React.FC<HotkeyHelpModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { getActiveHotkeys } = useHotkeyContext();
  const activeHotkeys = getActiveHotkeys();
  const grouped = useMemo(
    () => groupByCategory(activeHotkeys),
    [activeHotkeys]
  );

  // Fixed category order: Navigation first, then alphabetical
  const categories = useMemo(() => {
    const cats = Object.keys(grouped);
    const nav = cats.filter((c) => c === "Navigation");
    const rest = cats.filter((c) => c !== "Navigation").sort();
    return [...nav, ...rest];
  }, [grouped]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-5">
          {categories.map((category) => (
            <div key={category}>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {category}
              </h3>
              <div className="space-y-1">
                {grouped[category].map((hotkey) => (
                  <div
                    key={hotkey.id}
                    className="flex items-center justify-between py-1.5"
                  >
                    <span className="text-sm">{hotkey.description}</span>
                    <kbd className="text-xs text-muted-foreground font-mono bg-surface-3 border border-border rounded px-1.5 py-0.5 ml-4 shrink-0">
                      {formatKeys(hotkey.keys)}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HotkeyHelpModal;
